/**
 * Park Shores Gifts — Theme JavaScript
 * Mobile nav, cart drawer, quantity, product media
 */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     Utilities
     -------------------------------------------------------------------------- */

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.from((ctx || document).querySelectorAll(sel));
  }

  function trapFocus(container) {
    const focusable = qsa(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      container
    ).filter((el) => el.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function onKey(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKey);
    return function release() {
      container.removeEventListener('keydown', onKey);
    };
  }

  /* --------------------------------------------------------------------------
     Mobile navigation
     -------------------------------------------------------------------------- */

  function initMobileNav() {
    const toggle = qs('[data-mobile-nav-toggle]');
    const nav = qs('[data-mobile-nav]');
    const closeBtn = qs('[data-mobile-nav-close]');
    const overlay = qs('[data-mobile-nav-overlay]');

    if (!toggle || !nav) return;

    let releaseFocus = null;
    let lockedScrollY = 0;
    let opener = null;

    function lockPageScroll() {
      lockedScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
      document.documentElement.classList.add('is-scroll-locked');
      document.documentElement.style.scrollBehavior = 'auto';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + lockedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    function unlockPageScroll() {
      const y = lockedScrollY;
      const root = document.documentElement;
      root.style.scrollBehavior = 'auto';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      root.classList.remove('is-scroll-locked');
      if (typeof window.scrollTo === 'function') {
        window.scrollTo({ top: y, left: 0, behavior: 'instant' });
        if ((window.scrollY || window.pageYOffset) !== y) {
          window.scrollTo(0, y);
        }
      }
      requestAnimationFrame(function () {
        root.style.scrollBehavior = '';
      });
    }

    function open(fullPage, trigger) {
      opener = trigger || document.activeElement;
      lockPageScroll();
      nav.classList.add('is-open');
      nav.classList.toggle('is-fullpage', !!fullPage);
      nav.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      if (overlay) {
        overlay.hidden = false;
        overlay.removeAttribute('hidden');
        overlay.classList.toggle('is-fullpage', !!fullPage);
      }
      releaseFocus = trapFocus(nav);
      if (closeBtn) {
        try {
          closeBtn.focus({ preventScroll: true });
        } catch (e) {
          closeBtn.focus();
        }
      }
    }

    function close() {
      nav.classList.remove('is-open', 'is-fullpage');
      nav.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      if (overlay) {
        overlay.hidden = true;
        overlay.setAttribute('hidden', '');
        overlay.classList.remove('is-fullpage');
      }
      if (releaseFocus) releaseFocus();

      if (document.activeElement && nav.contains(document.activeElement)) {
        try {
          document.activeElement.blur();
        } catch (e) {}
      }

      unlockPageScroll();

      if (opener && typeof opener.focus === 'function') {
        try {
          opener.focus({ preventScroll: true });
        } catch (e) {}
      }
      opener = null;
    }

    window.ParkShores = window.ParkShores || {};
    window.ParkShores.openNav = open;
    window.ParkShores.closeNav = close;

    toggle.addEventListener('click', () => {
      if (toggle.getAttribute('aria-expanded') === 'true') close();
      else open(false, toggle);
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);

    nav.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });

    qsa('[data-open-full-menu]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        open(true, btn);
      });
    });

    /* Expand / 2nd-click navigate / +/- collapse for nested menu groups */
    function setGroupOpen(group, isOpen) {
      const panel = qs(':scope > .menu-group__panel', group);
      const icon = qs(':scope > .menu-group__row .menu-group__icon', group);
      const label = qs(':scope > .menu-group__row .menu-group__label', group);
      group.classList.toggle('is-open', isOpen);
      if (panel) panel.hidden = !isOpen;
      if (icon) {
        icon.textContent = isOpen ? '−' : '+';
        icon.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
      if (label) label.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    qsa('[data-menu-group]').forEach((group) => {
      const label = qs(':scope > .menu-group__row .menu-group__label', group);
      const icon = qs(':scope > .menu-group__row .menu-group__icon', group);
      const href = group.getAttribute('data-group-href');

      if (label) {
        label.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = group.classList.contains('is-open');
          if (!isOpen) setGroupOpen(group, true);
          else if (href && href !== '#') window.location.href = href;
        });
      }
      if (icon) {
        icon.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          setGroupOpen(group, !group.classList.contains('is-open'));
        });
      }
    });
  }

  function initHomepageChrome() {
    /*
      Height of Herend / Tabletop / Personalized = the real pixel
      distance from the bottom of the hero video to the top of Herend
      (the whole swipe section sitting between the two videos).
    */
    function syncHomeBlockHeight() {
      const video = qs('.video-hero');
      const herend = qs('.herend-stage');
      if (!video || !herend) return;
      const space = Math.round(herend.getBoundingClientRect().top - video.getBoundingClientRect().bottom);
      if (space > 40) {
        document.documentElement.style.setProperty('--home-block-height', space + 'px');
      }
    }
    syncHomeBlockHeight();
    window.addEventListener('resize', syncHomeBlockHeight);
    if (window.ResizeObserver) {
      const swipe = qs('.category-strip') || qs('.section-category-carousel');
      if (swipe) {
        const ro = new ResizeObserver(syncHomeBlockHeight);
        ro.observe(swipe);
      }
    }

    /* Hero video autoplay */
    qsa('[data-hero-video], .video-hero__video').forEach((v) => {
      v.muted = true;
      v.defaultMuted = true;
      v.loop = true;
      v.controls = false;
      v.disablePictureInPicture = true;
      const play = () => {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      };
      play();
      document.addEventListener('pointerdown', play, { once: true, passive: true });
      // If the browser ever stops the loop (loop unsupported, stalled restart, tab backgrounded),
      // start it again ourselves so the poster or a native play overlay never surfaces.
      v.addEventListener('ended', () => { v.currentTime = 0; play(); });
      v.addEventListener('pause', () => { if (!document.hidden) play(); });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) play(); });
    });

    /* Soft scroll for # anchors */
    // Gentle ease-in (owner's request 2026-09-07): a slightly concave curve, so the page starts moving
    // at about 40% of its average speed and builds gradually, arriving at ~1.6x. No slow-down at the end.
    function easeIn(t) {
      return t * (0.4 + 0.6 * t);
    }
    function smoothScrollTo(el, duration) {
      if (!el) return;
      duration = duration || 420;
      const header = qs('.site-header-wrap') || qs('.header-wrapper');
      const barH = header ? header.offsetHeight : 0;
      const startY = window.pageYOffset;
      const targetY = el.getBoundingClientRect().top + startY - barH - 8;
      const dist = targetY - startY;
      if (Math.abs(dist) < 2) return;
      // The page sets CSS scroll-behavior: smooth, which would smooth every step of this animation on top of
      // our easing. Turn it off for the duration and put it back after.
      const html = document.documentElement;
      const prevBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      let startTime = null;
      function step(ts) {
        if (!startTime) startTime = ts;
        const p = Math.min((ts - startTime) / duration, 1);
        window.scrollTo(0, startY + dist * easeIn(p));
        if (p < 1) requestAnimationFrame(step);
        else html.style.scrollBehavior = prevBehavior;
      }
      requestAnimationFrame(step);
    }
    qsa('[data-scroll-to]').forEach((link) => {
      link.addEventListener('click', (e) => {
        const sel = link.getAttribute('data-scroll-to') || link.getAttribute('href');
        if (!sel || sel.charAt(0) !== '#') return;
        const target = qs(sel);
        if (!target) return;
        e.preventDefault();
        smoothScrollTo(target, 420);
      });
    });

    /* Back to top */
    const backTop = qs('[data-back-to-top], #back-to-top');
    if (backTop) {
      const update = () => {
        backTop.hidden = window.pageYOffset < 480;
      };
      window.addEventListener('scroll', update, { passive: true });
      update();
      backTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    }

    /* Welcome modal */
    const welcome = qs('#welcome-modal');
    const KEY = 'ps-welcome-dismissed';
    function dismissWelcome() {
      if (!welcome) return;
      welcome.hidden = true;
      try {
        localStorage.setItem(KEY, '1');
      } catch (e) {}
    }
    if (welcome) {
      try {
        if (localStorage.getItem(KEY) !== '1') {
          setTimeout(() => {
            welcome.hidden = false;
          }, 1800);
        }
      } catch (e) {}
      qsa('[data-welcome-dismiss]', welcome).forEach((el) => el.addEventListener('click', dismissWelcome));
      const form = qs('#welcome-form', welcome) || qs('form', welcome);
      if (form) {
        form.addEventListener('submit', () => {
          /* allow native customer form post; also dismiss */
          try {
            localStorage.setItem(KEY, '1');
          } catch (e) {}
        });
      }
    }

    /* Herend YouTube stage.
       The frame is oversized by CSS so YouTube's title bar and logo sit outside the box, and a
       poster covers it until the video is really playing and YouTube's overlay has faded.
       The loop is done by seeking back just before the end: a natural end or the playlist
       restart would bring the title text back. */
    const yt = qs('#herend-yt');
    if (yt) {
      const stage = yt.closest('.herend-stage');
      const src = yt.getAttribute('src') || '';
      const idMatch = src.match(/embed\/([^?]+)/);
      const id = idMatch ? idMatch[1] : 'kT4D5WhubG8';
      const startMatch = src.match(/start=(\d+)/);
      const start = parseInt(yt.dataset.start || (startMatch ? startMatch[1] : '0'), 10) || 0;
      if (window.location.origin && window.location.origin.indexOf('file:') !== 0) {
        yt.src =
          'https://www.youtube.com/embed/' +
          id +
          '?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&modestbranding=1&loop=1&playlist=' +
          id +
          '&start=' +
          start +
          '&enablejsapi=1&origin=' +
          encodeURIComponent(window.location.origin);
      }

      let player = null;
      let revealTimer = null;
      const PLAYING = 1;
      const ENDED = 0;
      const setPlaying = (on) => {
        clearTimeout(revealTimer);
        if (!stage) return;
        if (on) {
          // YouTube hides its title overlay about three seconds after playback starts.
          revealTimer = setTimeout(() => stage.classList.add('is-playing'), 3200);
        } else {
          stage.classList.remove('is-playing');
        }
      };
      const play = () => {
        try {
          if (player && typeof player.playVideo === 'function') {
            player.mute();
            player.playVideo();
          }
        } catch (e) {}
      };
      const onReady = () => {
        play();
        setInterval(() => {
          try {
            if (!player || player.getPlayerState() !== PLAYING) return;
            const d = player.getDuration();
            if (d > 0 && player.getCurrentTime() > d - 0.8) player.seekTo(start, true);
          } catch (e) {}
        }, 250);
      };
      const onStateChange = (e) => {
        if (e.data === PLAYING) {
          setPlaying(true);
        } else if (e.data === ENDED) {
          setPlaying(false);
          try {
            player.seekTo(start, true);
          } catch (err) {}
          play();
        } else if (e.data !== 3) {
          // paused, cued, unstarted (3 = buffering, keep whatever is showing)
          setPlaying(false);
        }
      };
      const boot = () => {
        try {
          player = new window.YT.Player('herend-yt', { events: { onReady, onStateChange } });
        } catch (e) {}
      };
      if (window.YT && window.YT.Player) {
        boot();
      } else {
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (typeof prev === 'function') prev();
          boot();
        };
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.async = true;
        document.head.appendChild(tag);
      }
      document.addEventListener('pointerdown', play, { once: true, passive: true });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) play();
      });
    }
  }

  /* --------------------------------------------------------------------------
     Cart drawer
     -------------------------------------------------------------------------- */

  function initCartDrawer() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;

    const overlay = qs('[data-cart-drawer-overlay]', drawer);
    const closeBtn = qs('[data-cart-drawer-close]', drawer);
    let releaseFocus = null;

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      releaseFocus = trapFocus(drawer);
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (releaseFocus) releaseFocus();
    }

    window.ParkShores = window.ParkShores || {};
    window.ParkShores.openCartDrawer = open;
    window.ParkShores.closeCartDrawer = close;

    qsa('[data-cart-drawer-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);

    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
  }

  /* --------------------------------------------------------------------------
     Quantity controls
     -------------------------------------------------------------------------- */

  function initQuantity() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quantity-button]');
      if (!btn) return;

      const wrap = btn.closest('[data-quantity]');
      const input = qs('[data-quantity-input]', wrap);
      if (!input) return;

      const min = parseInt(input.getAttribute('min') || '1', 10);
      const max = parseInt(input.getAttribute('max') || '999', 10);
      let val = parseInt(input.value || '1', 10);

      if (btn.dataset.quantityButton === 'minus') {
        val = Math.max(min, val - 1);
      } else {
        val = Math.min(max, val + 1);
      }

      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* --------------------------------------------------------------------------
     Product media thumbnails
     -------------------------------------------------------------------------- */

  function setMainProductImage(src, srcset, mediaId, galleryRoot) {
    const scope = galleryRoot || document;
    const main = qs('[data-product-media-main]', scope) || qs('[data-product-media-main]');
    if (!main || !src) return;
    const mainImg = qs('img', main);
    if (!mainImg) return;

    mainImg.src = src;
    if (srcset) {
      mainImg.srcset = srcset;
    } else {
      mainImg.removeAttribute('srcset');
    }

    if (mediaId != null) {
      const thumbs = qsa('[data-product-media-thumb]', scope);
      let activeThumb = null;
      thumbs.forEach((t) => {
        const on = String(t.dataset.mediaId) === String(mediaId);
        t.classList.toggle('is-active', on);
        if (on) activeThumb = t;
      });
      // Keep the active thumb visible in the horizontal strip
      if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
        activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      }
    }
  }

  function getVariantMediaId(variant) {
    if (!variant) return null;
    return (
      variant.featured_media_id ||
      (variant.featured_media && variant.featured_media.id) ||
      (variant.featured_image && variant.featured_image.id) ||
      null
    );
  }

  function selectMediaVariantIfAssigned(form, mediaId) {
    if (
      form &&
      mediaId &&
      window.ParkShores &&
      typeof window.ParkShores.selectVariantByMediaId === 'function'
    ) {
      window.ParkShores.selectVariantByMediaId(mediaId, form, { skipMedia: true });
    }
  }

  function initProductMedia() {
    qsa('[data-product-media]').forEach((gallery) => {
      const productRoot = gallery.closest('[data-product]') || document;
      const form = qs('[data-product-form]', productRoot);
      const main = qs('[data-product-media-main]', gallery);
      const thumbs = qsa('[data-product-media-thumb]', gallery);

      function getMediaItems() {
        return thumbs.map((t) => ({
          mediaId: t.dataset.mediaId,
          src: t.dataset.fullSrc || qs('img', t)?.src || '',
          srcset: t.dataset.fullSrcset || '',
          el: t,
        }));
      }

      function getCurrentIndex(items) {
        if (!items.length) return 0;
        const activeIdx = items.findIndex((item) => item.el.classList.contains('is-active'));
        if (activeIdx >= 0) return activeIdx;

        const mainImg = main && qs('img', main);
        if (mainImg) {
          const src = mainImg.currentSrc || mainImg.src || '';
          const bySrc = items.findIndex(
            (item) => item.src && (src === item.src || src.includes(item.mediaId) || item.src.split('?')[0] === src.split('?')[0])
          );
          if (bySrc >= 0) return bySrc;
        }
        return 0;
      }

      function showMediaAt(index) {
        const items = getMediaItems();
        if (!items.length) return;
        // Hard stop — no circular wrap
        const i = Math.max(0, Math.min(items.length - 1, index));
        const item = items[i];
        if (!item || !item.src) return;
        setMainProductImage(item.src, item.srcset, item.mediaId, gallery);
        selectMediaVariantIfAssigned(form, item.mediaId);
      }

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => {
          showMediaAt(index);
        });
      });

      // Horizontal swipe on main image (mobile) — same media sequence as thumbs
      if (main && thumbs.length > 1) {
        main.setAttribute('data-swipeable', 'true');
        let startX = 0;
        let startY = 0;
        let tracking = false;

        main.addEventListener(
          'touchstart',
          (e) => {
            if (e.touches.length !== 1) return;
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            tracking = true;
          },
          { passive: true }
        );

        main.addEventListener(
          'touchend',
          (e) => {
            if (!tracking) return;
            tracking = false;
            const touch = e.changedTouches[0];
            if (!touch) return;

            const dx = touch.clientX - startX;
            const dy = touch.clientY - startY;
            const absX = Math.abs(dx);
            const absY = Math.abs(dy);

            // Require a clear horizontal swipe
            if (absX < 40 || absX < absY) return;

            const items = getMediaItems();
            const idx = getCurrentIndex(items);
            // Swipe left → next image; swipe right → previous (hard stops at ends)
            if (dx < 0) {
              showMediaAt(idx + 1);
            } else {
              showMediaAt(idx - 1);
            }
          },
          { passive: true }
        );
      }
    });
  }

  /* --------------------------------------------------------------------------
     Variant picker (product form)
     -------------------------------------------------------------------------- */

  function initVariantPicker() {
    window.ParkShores = window.ParkShores || {};

    qsa('[data-product-form]').forEach((form) => {
      const productRoot = form.closest('[data-product]') || document;
      const variantsScript = qs('[data-product-variants]', form) || qs('[data-product-variants]', productRoot);
      if (!variantsScript) return;

      let variants = [];
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (err) {
        return;
      }

      // Optional media map: { "imageId": { src, srcset } }
      let mediaMap = {};
      const mediaScript = qs('[data-product-media-map]', productRoot);
      if (mediaScript) {
        try {
          mediaMap = JSON.parse(mediaScript.textContent);
        } catch (err) {
          mediaMap = {};
        }
      }

      const idInput = qs('[name="id"]', form);
      const priceEl = qs('[data-product-price]', productRoot);
      const addBtn = qs('[data-add-to-cart]', form);
      const availabilityNote = qs('[data-availability-note]', productRoot);
      const orderInNote = qs('[data-order-in-note]', productRoot);
      const inShopNote = qs('[data-in-shop-note]', productRoot);
      const optionSelects = qsa('[data-option-select]', form);
      const optionButtons = qsa('[data-option-button]', form);

      function getSelectedOptions() {
        const groups = {};

        optionSelects.forEach((sel) => {
          groups[String(sel.dataset.optionIndex)] = sel.value;
        });

        optionButtons
          .filter((b) => b.classList.contains('is-selected'))
          .forEach((b) => {
            groups[String(b.dataset.optionIndex)] = b.dataset.value;
          });

        return Object.keys(groups)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => groups[k]);
      }

      function findVariant(options) {
        return variants.find((v) => {
          const opts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
          return options.every((opt, i) => opts[i] === opt);
        });
      }

      function findVariantByMediaId(mediaId) {
        if (mediaId == null || mediaId === '') return null;
        const idStr = String(mediaId);
        return (
          variants.find((v) => String(getVariantMediaId(v)) === idStr) || null
        );
      }

      function applyOptionButtonsFromVariant(variant) {
        if (!variant) return;
        const opts = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
        opts.forEach((optValue, index) => {
          qsa('[data-option-button][data-option-index="' + index + '"]', form).forEach((btn) => {
            btn.classList.toggle('is-selected', btn.dataset.value === optValue);
          });
          const sel = qs('[data-option-select][data-option-index="' + index + '"]', form);
          if (sel) sel.value = optValue;
        });
      }

      function updateVariantMedia(variant) {
        if (!variant) return;

        const mediaId = getVariantMediaId(variant);
        if (mediaId == null) return;

        const mapped = mediaMap[mediaId] || mediaMap[String(mediaId)];
        if (mapped && mapped.src) {
          setMainProductImage(mapped.src, mapped.srcset || '', mediaId);
          return;
        }

        if (variant.featured_image && variant.featured_image.src) {
          setMainProductImage(variant.featured_image.src, '', mediaId);
          return;
        }

        const thumb = qs('[data-product-media-thumb][data-media-id="' + mediaId + '"]', productRoot);
        if (thumb) {
          setMainProductImage(
            thumb.dataset.fullSrc || qs('img', thumb)?.src,
            thumb.dataset.fullSrcset || '',
            mediaId
          );
        }
      }

      function updateUI(variant, options) {
        const opts = options || {};
        if (!variant) {
          if (addBtn) {
            addBtn.disabled = true;
            addBtn.textContent = addBtn.dataset.unavailableText || 'Unavailable';
          }
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (priceEl) {
          priceEl.innerHTML = formatPrice(variant);
        }

        // In the shop vs. ordered in, for variants we keep selling at zero stock
        if (orderInNote || inShopNote) {
          if (orderInNote) orderInNote.hidden = !variant.order_in;
          if (inShopNote) inShopNote.hidden = !!variant.order_in;
          if (availabilityNote && orderInNote) availabilityNote.hidden = !!variant.order_in;
        }

        if (addBtn) {
          if (variant.available) {
            addBtn.disabled = false;
            addBtn.textContent = addBtn.dataset.addText || 'Add to cart';
          } else {
            addBtn.disabled = true;
            addBtn.textContent = addBtn.dataset.soldOutText || 'Sold out';
          }
        }

        if (!opts.skipMedia) {
          updateVariantMedia(variant);
        }

        // Update URL without reload
        if (variant.id && history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }
      }

      function formatPrice(variant) {
        const money = (cents) => {
          try {
            return (cents / 100).toLocaleString(undefined, {
              style: 'currency',
              currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
            });
          } catch (e) {
            return '$' + (cents / 100).toFixed(2);
          }
        };

        if (variant.compare_at_price > variant.price) {
          return (
            '<span class="price--sale">' +
            money(variant.price) +
            '</span> <span class="price--compare">' +
            money(variant.compare_at_price) +
            '</span>'
          );
        }
        return money(variant.price);
      }

      function onOptionChange() {
        const options = getSelectedOptions();
        const variant = findVariant(options);
        updateUI(variant);
      }

      /**
       * Select a variant from a product media id (thumbnail click).
       * Returns true if a matching variant was found and selected.
       */
      function selectVariantByMediaId(mediaId, targetForm, options) {
        if (targetForm && targetForm !== form) return false;
        const variant = findVariantByMediaId(mediaId);
        if (!variant) return false; // image not assigned to a variant — do nothing to selector
        applyOptionButtonsFromVariant(variant);
        updateUI(variant, options || {});
        return true;
      }

      // Register globally (last form wins; fine for single product page)
      window.ParkShores.selectVariantByMediaId = selectVariantByMediaId;

      optionSelects.forEach((sel) => {
        sel.addEventListener('change', onOptionChange);
      });

      optionButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const group = btn.dataset.optionIndex;
          qsa('[data-option-button][data-option-index="' + group + '"]', form).forEach((b) =>
            b.classList.remove('is-selected')
          );
          btn.classList.add('is-selected');
          onOptionChange();
        });
      });

      // Opening on a special-order option while another is in the shop: start on the one in the shop
      // (unless the link asked for a variant)
      if (orderInNote && !new URL(window.location.href).searchParams.has('variant')) {
        const current = variants.find((v) => String(v.id) === String(idInput && idInput.value));
        const inShop = variants.find((v) => !v.order_in);
        if (current && current.order_in && inShop) {
          applyOptionButtonsFromVariant(inShop);
          updateUI(inShop);
        }
      }
    });
  }

  /* --------------------------------------------------------------------------
     AJAX add to cart (drawer themes)
     -------------------------------------------------------------------------- */

  function initAddToCart() {
    // Exposed so sections that add several items at once (Herend place settings) can refresh the drawer
    window.ParkShores = window.ParkShores || {};
    window.ParkShores.refreshCartDrawer = async () => {
      await refreshCartDrawer();
      updateCartCount();
    };

    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-product-form], [data-quick-add-form]');
      if (!form) return;

      // Online quantity limit (products tagged qtylim / qtylim-N, see snippets/qty-limit.liquid)
      const qtyLimit = parseInt(form.dataset.qtyLimit || '', 10);
      // Only intercept when cart drawer is available (or a limit has to be checked first)
      const useDrawer =
        !!(window.ParkShores && window.ParkShores.openCartDrawer) && !!document.querySelector('[data-cart-drawer]');
      if (!useDrawer && !(qtyLimit > 0)) return;

      e.preventDefault();

      if (qtyLimit > 0 && !(await applyQtyLimit(form, qtyLimit))) return;
      if (!useDrawer) {
        form.submit();
        return;
      }

      const btn = qs('[type="submit"]', form);
      const originalText = btn ? btn.textContent : '';
      if (btn) {
        btn.disabled = true;
        btn.textContent = btn.dataset.addingText || 'Adding…';
      }

      try {
        const formData = new FormData(form);
        const res = await fetch(window.Shopify?.routes?.root
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });

        if (!res.ok) throw new Error('Add to cart failed');

        await refreshCartDrawer();
        updateCartCount();
        window.ParkShores.openCartDrawer();
      } catch (err) {
        console.error(err);
        // Fallback: native submit
        form.submit();
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.textContent = originalText;
        }
      }
    });
  }

  // Trims the quantity so this product's total in the cart stays within the limit.
  // Returns false (and says why) when the cart already holds the limit.
  async function applyQtyLimit(form, limit) {
    let inCart = 0;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const productId = parseInt(form.dataset.productId || '0', 10);
      (cart.items || []).forEach((item) => {
        if (item.product_id === productId) inCart += item.quantity;
      });
    } catch (err) {
      return true; // cart unreadable: do not block the sale
    }

    const input = form.querySelector('[name="quantity"]');
    const wanted = input ? Math.max(1, parseInt(input.value || '1', 10)) : 1;
    const allowed = limit - inCart;

    if (allowed <= 0) {
      showQtyLimitNote(
        form,
        'Limited to ' + limit + ' per customer. You already have ' + inCart + ' in your cart.'
      );
      return false;
    }
    if (wanted > allowed) {
      if (input) input.value = allowed;
      showQtyLimitNote(form, 'Limited to ' + limit + ' per customer, so we added ' + allowed + '.');
    } else {
      showQtyLimitNote(form, '');
    }
    return true;
  }

  function showQtyLimitNote(form, text) {
    let note = form.querySelector('[data-qty-limit-note]');
    if (!text) {
      if (note) note.remove();
      return;
    }
    if (!note) {
      note = document.createElement('p');
      note.className = 'qty-limit-note body-sm';
      note.setAttribute('role', 'status');
      note.setAttribute('data-qty-limit-note', '');
      form.appendChild(note);
    }
    note.textContent = text;
  }

  async function refreshCartDrawer() {
    const drawerBody = qs('[data-cart-drawer-body]');
    if (!drawerBody) return;

    try {
      // Section Rendering API: Liquid draws the lines, so pickup notes and the notice stay in one place
      const res = await fetch(storefrontRoot() + 'cart?section_id=cart-drawer-content');
      if (!res.ok) throw new Error('Drawer section failed');

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const content = qs('[data-cart-drawer-content]', doc);
      if (!content) throw new Error('Drawer section empty');

      drawerBody.innerHTML = qs('[data-cart-drawer-body]', content).innerHTML;
      qs('[data-cart-drawer-subtotal]').textContent = qs('[data-cart-drawer-subtotal]', content).textContent;
      qs('[data-cart-drawer] [data-pickup-notice]').replaceWith(qs('[data-pickup-notice]', content));
      qs('[data-cart-drawer-footer]').hidden = content.dataset.itemCount === '0';
      settlePickupNotes();
    } catch (e) {
      await rebuildDrawerFromJson();
    }
  }

  function storefrontRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  /* --------------------------------------------------------------------------
     Pickup notice: Liquid cannot see store availability for every variant in the
     cart (see snippets/pickup-line-status.liquid), so ask for the lines it left
     unknown, then recount. A failed lookup leaves the line without a note.
     -------------------------------------------------------------------------- */

  const pickupStatuses = new Map();

  function pickupStatusFor(variantId) {
    if (!pickupStatuses.has(variantId)) {
      pickupStatuses.set(
        variantId,
        fetch(storefrontRoot() + 'variants/' + variantId + '/?section_id=pickup-check')
          .then((res) => (res.ok ? res.text() : ''))
          .then((html) => {
            const el = qs('[data-pickup-status]', new DOMParser().parseFromString(html, 'text/html'));
            return el ? el.dataset.pickupStatus : 'unknown';
          })
          .catch(() => 'unknown')
      );
    }
    return pickupStatuses.get(variantId);
  }

  async function settlePickupNotes() {
    const notes = qsa('[data-pickup-note][data-pickup-status="unknown"]');
    if (!notes.length) return;

    await Promise.all(
      notes.map(async (note) => {
        const status = await pickupStatusFor(note.dataset.variantId);
        note.dataset.pickupStatus = status;
        note.hidden = status !== 'warehouse';
      })
    );

    qsa('[data-pickup-notice]').forEach((notice) => {
      const scope = notice.closest('[data-pickup-scope]');
      const count = qsa('[data-pickup-note][data-pickup-status="warehouse"]', scope).length;
      const lead = qs('[data-pickup-notice-lead]', notice);
      lead.textContent = count === 1 ? notice.dataset.one : notice.dataset.other.replace('[count]', count);
      notice.hidden = count === 0;
    });
  }

  async function rebuildDrawerFromJson() {
    const drawerBody = qs('[data-cart-drawer-body]');
    const subtotalEl = qs('[data-cart-drawer-subtotal]');
    const footerEl = qs('[data-cart-drawer-footer]');
    const pickupNotice = qs('[data-cart-drawer] [data-pickup-notice]');
    if (!drawerBody) return;
    if (pickupNotice) pickupNotice.hidden = true;

    const res = await fetch('/cart.js');
    const cart = await res.json();

    if (!cart.items || cart.items.length === 0) {
      drawerBody.innerHTML =
        '<div class="cart-drawer__empty"><p>Your cart is empty.</p><a class="button button--primary mt-3" href="/collections/all">Continue shopping</a></div>';
      if (subtotalEl) subtotalEl.textContent = formatMoney(0);
      if (footerEl) footerEl.hidden = true;
      return;
    }
    if (footerEl) footerEl.hidden = false;

    drawerBody.innerHTML = cart.items
      .map((item) => {
        const img = item.image
          ? '<img src="' + item.image + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" width="80" height="80">'
          : '';
        return (
          '<div class="cart-item" data-line-key="' +
          item.key +
          '">' +
          '<div class="cart-item__image">' +
          img +
          '</div>' +
          '<div class="cart-item__details">' +
          '<a class="cart-item__title" href="' +
          item.url +
          '">' +
          escapeHtml(item.product_title) +
          '</a>' +
          (item.variant_title && item.variant_title !== 'Default Title'
            ? '<div class="cart-item__variant">' + escapeHtml(item.variant_title) + '</div>'
            : '') +
          '<div class="cart-item__price">' +
          formatMoney(item.final_line_price) +
          ' · Qty ' +
          item.quantity +
          '</div>' +
          '<button type="button" class="cart-item__remove" data-cart-remove="' +
          item.key +
          '">Remove</button>' +
          '</div></div>'
        );
      })
      .join('');

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
  }

  function formatMoney(cents) {
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      qsa('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (e) {
      /* ignore */
    }
  }

  function initCartRemove() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-cart-remove]');
      if (!btn) return;

      const key = btn.dataset.cartRemove;
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        await refreshCartDrawer();
        updateCartCount();
      } catch (err) {
        console.error(err);
      }
    });
  }

  /* --------------------------------------------------------------------------
     Init
     -------------------------------------------------------------------------- */

  /* --------------------------------------------------------------------------
     Collection card: variant thumbs
     - Hover / tap: swap main product image immediately
     - Hover 1s: show truncated variant name tooltip
     - Mobile: first tap previews; second tap on same thumb navigates
     -------------------------------------------------------------------------- */

  function initProductCardVariants() {
    const LABEL_MAX = 32;
    let activeTooltipThumb = null;

    function truncateLabel(label) {
      const text = (label || '').trim();
      if (text.length <= LABEL_MAX) return text;
      return text.slice(0, LABEL_MAX - 1).trimEnd() + '…';
    }

    function hideTooltip(thumb) {
      const target = thumb || activeTooltipThumb;
      if (!target) return;
      const tip = qs('[data-product-card-variant-tooltip]', target);
      if (tip) {
        tip.hidden = true;
        tip.textContent = '';
      }
      if (!thumb || thumb === activeTooltipThumb) {
        activeTooltipThumb = null;
      }
    }

    function showTooltip(thumb) {
      hideTooltip();
      activeTooltipThumb = thumb;
      const label = thumb.dataset.variantLabel || '';
      if (!label) return;
      const tip = qs('[data-product-card-variant-tooltip]', thumb);
      if (!tip) return;
      tip.textContent = truncateLabel(label);
      tip.hidden = false;
    }

    function applyVariantPreview(thumb) {
      const card = thumb.closest('[data-product-card]');
      if (!card) return;

      const mainImg = qs('[data-product-card-main-image]', card);
      if (!mainImg) return;

      if (!mainImg.dataset.originalSrc) {
        mainImg.dataset.originalSrc = mainImg.getAttribute('src') || '';
        mainImg.dataset.originalSrcset = mainImg.getAttribute('srcset') || '';
      }

      if (thumb.dataset.imageSrc) {
        mainImg.src = thumb.dataset.imageSrc;
        if (thumb.dataset.imageSrcset) {
          mainImg.srcset = thumb.dataset.imageSrcset;
        } else {
          mainImg.removeAttribute('srcset');
        }
      }

      card.classList.add('is-variant-previewing');
      qsa('[data-product-card-variant-thumb]', card).forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');

      // Point product links at this variant
      const href = thumb.getAttribute('href');
      if (href) {
        const mediaLink = qs('[data-product-card-media-link]', card);
        const titleLink = qs('[data-product-card-title-link]', card);
        const optionsLink = qs('[data-product-card-options-link]', card);
        if (mediaLink) mediaLink.href = href;
        if (titleLink) titleLink.href = href;
        if (optionsLink) optionsLink.href = href;
      }
    }

    // Immediate image swap on hover (desktop)
    document.addEventListener(
      'pointerenter',
      (e) => {
        if (e.pointerType === 'touch') return;
        const thumb = e.target.closest && e.target.closest('[data-product-card-variant-thumb]');
        if (!thumb) return;
        applyVariantPreview(thumb);
        showTooltip(thumb);
      },
      true
    );

    document.addEventListener(
      'pointerleave',
      (e) => {
        if (e.pointerType === 'touch') return;
        const thumb = e.target.closest && e.target.closest('[data-product-card-variant-thumb]');
        if (!thumb) return;
        hideTooltip(thumb);
      },
      true
    );

    // Keyboard focus
    document.addEventListener('focusin', (e) => {
      const thumb = e.target.closest && e.target.closest('[data-product-card-variant-thumb]');
      if (!thumb) return;
      applyVariantPreview(thumb);
      showTooltip(thumb);
    });

    document.addEventListener('focusout', (e) => {
      const thumb = e.target.closest && e.target.closest('[data-product-card-variant-thumb]');
      if (!thumb) return;
      hideTooltip(thumb);
    });

    // Mobile / click: first tap previews, second tap on same thumb goes to product
    document.addEventListener('click', (e) => {
      const thumb = e.target.closest && e.target.closest('[data-product-card-variant-thumb]');
      if (!thumb) return;

      const alreadyActive = thumb.classList.contains('is-active');
      const isCoarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;

      // On touch devices: first tap only previews. Second tap navigates.
      if (isCoarse || e.pointerType === 'touch') {
        if (!alreadyActive) {
          e.preventDefault();
          applyVariantPreview(thumb);
          showTooltip(thumb);
        }
        // else: allow default navigation to product?variant=
      } else {
        // Desktop: still swap image if they click without hovering first
        applyVariantPreview(thumb);
        showTooltip(thumb);
      }
    });

    // Mobile: any scroll dismisses the variant title tooltip
    function dismissTooltipOnScroll() {
      if (!activeTooltipThumb) return;
      hideTooltip();
    }

    window.addEventListener('scroll', dismissTooltipOnScroll, { passive: true, capture: true });
    document.addEventListener('touchmove', dismissTooltipOnScroll, { passive: true });
  }

  /* --------------------------------------------------------------------------
     Site search: header button → panel with live suggestions
     -------------------------------------------------------------------------- */

  function initSearch() {
    const panel = qs('[data-search-panel]');
    const overlay = qs('[data-search-overlay]');
    const input = qs('[data-search-input]', panel);
    const results = qs('[data-search-results]', panel);
    const openBtns = qsa('[data-search-open]');
    if (!panel || !input || !results || !openBtns.length) return;

    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const money = (amount) => {
      const n = parseFloat(amount);
      if (isNaN(n)) return '';
      try {
        return n.toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' });
      } catch (e) { return '$' + n.toFixed(2); }
    };
    const searchUrl = (q) => '/search?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';
    const collectionItem = (c) => '<li><a class="search-panel__link" href="' + esc(c.url) + '">' + esc(c.title.replace(/^Herend /, '')) + (/^Herend /.test(c.title) ? '<small>Herend</small>' : '') + '</a></li>';
    const suggestUrl = (q, types, limit) => '/search/suggest.json?q=' + encodeURIComponent(q)
      + '&resources[type]=' + types + '&resources[limit]=' + limit + '&resources[limit_scope]=each'
      + '&resources[options][prefix]=last&resources[options][unavailable_products]=hide'
      + '&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,tag';

    // Results page: Shopify's page search returns products only, so fetch the matching patterns here
    const pageGroup = qs('[data-search-collections]');
    if (pageGroup && pageGroup.dataset.query) {
      fetch(suggestUrl(pageGroup.dataset.query, 'collection', 10), { headers: { Accept: 'application/json' } })
        .then((res) => res.json())
        .then((data) => {
          const cols = (data && data.resources && data.resources.results && data.resources.results.collections) || [];
          if (!cols.length) return;
          qs('[data-search-collections-list]', pageGroup).innerHTML = cols.map(collectionItem).join('');
          pageGroup.hidden = false;
          const t = qs('[data-search-products-title]'); if (t) t.hidden = false;
        })
        .catch(() => {});
    }

    let open = false;
    let timer = null;
    let controller = null;
    let lastQuery = '';

    function show() {
      if (open) return;
      open = true;
      panel.hidden = false;
      overlay.hidden = false;
      openBtns.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      input.focus();
      input.select();
    }
    function hide() {
      if (!open) return;
      open = false;
      panel.hidden = true;
      overlay.hidden = true;
      openBtns.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (controller) controller.abort();
    }

    function render(q, data) {
      const r = (data && data.resources && data.resources.results) || {};
      const collections = r.collections || [];
      const products = r.products || [];
      if (!collections.length && !products.length) {
        results.className = 'search-panel__results';
        results.innerHTML = '<p class="search-panel__empty">Nothing yet for “' + esc(q) + '”. Keep typing, or press Enter to search everything.</p>';
        return;
      }
      let html = '';
      if (collections.length) {
        html += '<div><h3 class="search-panel__heading">Patterns &amp; collections</h3><ul class="search-panel__list">'
          + collections.map(collectionItem).join('') + '</ul></div>';
      }
      if (products.length) {
        html += '<div><h3 class="search-panel__heading">Pieces</h3><ul class="search-panel__products">'
          + products.map((p) => {
            const src = p.featured_image && p.featured_image.url ? p.featured_image.url : '';
            const img = src ? src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=160' : '';
            return '<li><a class="search-panel__product" href="' + esc(p.url) + '">'
              + (img ? '<img src="' + esc(img) + '" alt="" width="56" height="56">' : '<span></span>')
              + '<span><span class="search-panel__product-title">' + esc(p.title) + '</span>'
              + '<span class="search-panel__product-price">' + money(p.price) + '</span></span></a></li>';
          }).join('')
          + '</ul></div>';
      }
      html += '<a class="search-panel__all" href="' + searchUrl(q) + '">See all results for “' + esc(q) + '”</a>';
      results.className = 'search-panel__results' + (collections.length ? ' has-collections' : '');
      results.innerHTML = html;
    }

    function suggest() {
      const q = input.value.trim();
      if (q === lastQuery) return;
      lastQuery = q;
      if (controller) controller.abort();
      if (q.length < 2) { results.innerHTML = ''; results.className = 'search-panel__results'; return; }
      controller = new AbortController();
      fetch(suggestUrl(q, 'product,collection', 8), { signal: controller.signal, headers: { Accept: 'application/json' } })
        .then((res) => res.json())
        .then((data) => { if (input.value.trim() === q) render(q, data); })
        .catch(() => {});
    }

    openBtns.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); open ? hide() : show(); }));
    qsa('[data-search-close]', panel).forEach((b) => b.addEventListener('click', hide));
    overlay.addEventListener('click', hide);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && open) { hide(); return; }
      // "/" anywhere on the page opens search, like the big shops
      if (e.key === '/' && !open && !/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName)) { e.preventDefault(); show(); }
    });
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(suggest, 180); });
    qs('[data-search-form]', panel).addEventListener('submit', (e) => { if (input.value.trim().length < 1) e.preventDefault(); });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initSearch();
    initMobileNav();
    initCartDrawer();
    initQuantity();
    initProductMedia();
    initVariantPicker();
    initAddToCart();
    initCartRemove();
    initProductCardVariants();
    initHomepageChrome();
    updateCartCount();
    settlePickupNotes();
  });
})();

